import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchAdminsThunk,
  deleteAdminThunk,
  toggleBlockAdminThunk,
  clearAdminError,
  createAdminThunk,
  updateAdminPasswordThunk,
  getForgotPasswordRequestsThunk,
} from "../../store/thunks/adminThunks";

import { clearSuccessMessage } from "../../store/slices/adminSlice";
import {
  getAllAdminsWithPermissionsThunk,
  getPendingApprovalsThunk,
  getPermissionTemplatesThunk,
} from "../../store/thunks/permissionThunks";
import Button from "../../components/common/Button";
import Filter from "../../components/common/Filter";
import Tabs from "../../components/common/Tabs";
import { notifyError, notifySuccess } from "../../utils/notification";
import AdminCard from "./components/AdminCard";
import Modal from "../../components/common/Modal";
import AdminCreateModal from "./components/AdminCreateModal";
import AdminResetPasswordModal from "./components/AdminResetPasswordModal";
import PermissionModal from "./components/PermissionModal";
import BranchAccessModal from "./components/BranchAccessModal";
import ApprovalRequestsPanel from "./components/ApprovalRequestsPanel";
import {
  UserGroupIcon,
  ShieldCheckIcon,
  KeyIcon,
  ClipboardCheckIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";

const adminFilterStatus = {
  All: {},
  ForgotPasswordRequests: { forgotPassword: true },
  Blocked: { isBlocked: true },
  Active: { isBlocked: false, forgotPassword: false },
};

const AdminManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get data from auth state instead of admin state
  const { user, isRootAdmin, requiresInitialSetup, systemInitStatus } =
    useSelector((state) => state.auth);

  const { admins, loading, error, successMessage, forgotPasswordRequests } =
    useSelector((state) => state.admin);

  const { adminsWithPermissions, pendingApprovals, permissionTemplates } =
    useSelector((state) => state.adminPermission || state.adminPermission);

  const { branches } = useSelector((state) => state.branch);

  // Use ref to track initial load
  const hasLoadedRef = useRef(false);

  // State
  const [activeTab, setActiveTab] = useState("admins");
  const [currentFilter, setCurrentFilter] = useState("All");
  const [selectedAdmins, setSelectedAdmins] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
  const [isBranchAccessModalOpen, setIsBranchAccessModalOpen] = useState(false);

  const [adminForReset, setAdminForReset] = useState(null);
  const [adminForPermission, setAdminForPermission] = useState(null);
  const [adminForBranchAccess, setAdminForBranchAccess] = useState(null);

  // Check for initial setup requirement and redirect
  useEffect(() => {
    if (requiresInitialSetup && isRootAdmin) {
      navigate("/initial-setup");
    }
  }, [requiresInitialSetup, isRootAdmin, navigate]);

  // Load data only once on mount for root admin
  useEffect(() => {
    if (isRootAdmin && !hasLoadedRef.current) {
      hasLoadedRef.current = true;

      // Load initial data
      dispatch(getPermissionTemplatesThunk());
      dispatch(fetchAdminsThunk(adminFilterStatus.All));
      dispatch(getAllAdminsWithPermissionsThunk());
      dispatch(getPendingApprovalsThunk());
    }
  }, [isRootAdmin, dispatch]);

  // Handle filter changes (not on initial load)
  useEffect(() => {
    // Skip on initial mount - initial load is handled by the previous useEffect
    if (!hasLoadedRef.current) return;

    if (isRootAdmin) {
      if (currentFilter === "ForgotPasswordRequests") {
        dispatch(getForgotPasswordRequestsThunk());
      } else {
        dispatch(fetchAdminsThunk(adminFilterStatus[currentFilter]));
      }
    }
  }, [currentFilter, isRootAdmin, dispatch]);

  // Error handling
  useEffect(() => {
    if (error) {
      notifyError(error);
      const timer = setTimeout(() => dispatch(clearAdminError()), 100);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Success handling
  useEffect(() => {
    if (successMessage) {
      notifySuccess(successMessage);
      const timer = setTimeout(() => dispatch(clearSuccessMessage()), 100);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Handlers
  const handleFilterChange = useCallback((status) => {
    setCurrentFilter(status);
    setSelectedAdmins([]);
  }, []);

  const handleSingleAction = useCallback(
    async (adminCode, action) => {
      const confirmMessage =
        action === "delete"
          ? "Are you sure you want to delete this admin?"
          : action === "block"
          ? "Are you sure you want to block this admin?"
          : "Are you sure you want to unblock this admin?";

      if (window.confirm(confirmMessage)) {
        try {
          if (action === "delete") {
            await dispatch(deleteAdminThunk(adminCode)).unwrap();
          } else {
            await dispatch(
              toggleBlockAdminThunk({
                adminCode,
                block: action === "block",
              })
            ).unwrap();
          }
          // Refresh the list
          dispatch(fetchAdminsThunk(adminFilterStatus[currentFilter]));
        } catch (error) {
          console.error("Action failed:", error);
        }
      }
    },
    [currentFilter, dispatch]
  );

  const openCreateModal = useCallback(() => setIsCreateModalOpen(true), []);

  const closeCreateModal = useCallback(() => {
    if (window.confirm("Are you sure you want to close without saving?")) {
      setIsCreateModalOpen(false);
    }
  }, []);

  const openResetPasswordModal = useCallback((admin) => {
    setAdminForReset(admin);
    setIsResetPasswordModalOpen(true);
  }, []);

  const closeResetPasswordModal = useCallback(() => {
    if (window.confirm("Are you sure you want to close without saving?")) {
      setIsResetPasswordModalOpen(false);
      setAdminForReset(null);
    }
  }, []);

  const openPermissionModal = useCallback((admin) => {
    setAdminForPermission(admin);
    setIsPermissionModalOpen(true);
  }, []);

  const closePermissionModal = useCallback(() => {
    setIsPermissionModalOpen(false);
    setAdminForPermission(null);
  }, []);

  // NEW: Branch Access Modal handlers
  const openBranchAccessModal = useCallback((admin) => {
    setAdminForBranchAccess(admin);
    setIsBranchAccessModalOpen(true);
  }, []);

  const closeBranchAccessModal = useCallback(() => {
    setIsBranchAccessModalOpen(false);
    setAdminForBranchAccess(null);
    // Refresh admin list to get updated branch access
    dispatch(fetchAdminsThunk(adminFilterStatus[currentFilter]));
  }, [currentFilter, dispatch]);

  const handleCreateAdmin = useCallback(
    async (adminData) => {
      try {
        await dispatch(createAdminThunk(adminData)).unwrap();
        setIsCreateModalOpen(false);
        dispatch(fetchAdminsThunk(adminFilterStatus[currentFilter]));
      } catch (error) {
        console.error("Failed to create admin:", error);
      }
    },
    [currentFilter, dispatch]
  );

  const handleResetPassword = useCallback(
    async (data) => {
      try {
        await dispatch(updateAdminPasswordThunk(data)).unwrap();
        setIsResetPasswordModalOpen(false);
        setAdminForReset(null);
        if (currentFilter === "ForgotPasswordRequests") {
          dispatch(getForgotPasswordRequestsThunk());
        }
      } catch (error) {
        console.error("Failed to reset password:", error);
      }
    },
    [currentFilter, dispatch]
  );

  // Memoized filtered admins
  const filteredAdmins = useMemo(() => {
    let adminList =
      currentFilter === "ForgotPasswordRequests"
        ? forgotPasswordRequests
        : admins;

    if (!adminList) return [];

    if (searchQuery) {
      adminList = adminList.filter(
        (admin) =>
          admin?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          admin?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          admin?.adminCode?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return adminList;
  }, [admins, forgotPasswordRequests, currentFilter, searchQuery]);

  // Tab configuration
  const tabs = [
    {
      id: "admins",
      label: "Admin Users",
      icon: <UserGroupIcon className="w-4 h-4" />,
      badge: admins?.length || 0,
    },
    {
      id: "approvals",
      label: "Approvals",
      icon: <ClipboardCheckIcon className="w-4 h-4" />,
      badge: pendingApprovals?.length || 0,
      disabled: !isRootAdmin,
    },
  ];

  // Check access
  if (!isRootAdmin && user?.role !== "admin") {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <ShieldCheckIcon className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600">
            Only root admins and authorized admins can access this page. If you
            need access, please contact your system administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container mx-auto p-4 bg-background text-text">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShieldCheckIcon className="w-7 h-7 text-primary" />
              Admin Management
            </h1>
            <p className="text-gray-600 mt-1">
              Manage admin users, permissions, and branch access
            </p>
          </div>
          {isRootAdmin && !requiresInitialSetup && (
            <Button variant="primary" onClick={openCreateModal}>
              Create Admin
            </Button>
          )}
        </div>

        {/* Tabs */}
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={setActiveTab}
          className="mb-6"
        />

        {/* Tab Content */}
        {activeTab === "admins" && (
          <div>
            {/* Filters and Search */}
            <div className="sticky top-0 z-8 bg-background py-2 mb-4">
              <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                <Filter
                  defaultOption="All"
                  options={[
                    "All",
                    "Active",
                    "ForgotPasswordRequests",
                    "Blocked",
                  ]}
                  onSelect={handleFilterChange}
                />
                <input
                  type="text"
                  placeholder="Search admins..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            {/* Admin Cards */}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {filteredAdmins.map((admin) => (
                  <AdminCard
                    key={admin.adminCode}
                    admin={admin}
                    handleSingleAction={handleSingleAction}
                    openResetPasswordModal={openResetPasswordModal}
                    openPermissionModal={openPermissionModal}
                    openBranchAccessModal={openBranchAccessModal}
                    selected={selectedAdmins.includes(admin._id)}
                    currentFilter={currentFilter}
                    isRootAdmin={isRootAdmin}
                  />
                ))}
              </div>
            )}

            {!loading && filteredAdmins.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No admins found{" "}
                {currentFilter !== "All" && `for ${currentFilter}`}
              </div>
            )}
          </div>
        )}

        {activeTab === "permissions" && isRootAdmin && (
          <div className="grid grid-cols-1 gap-4">
            {(adminsWithPermissions || [])
              .filter((admin) => admin.role !== "root")
              .map((admin) => (
                <div key={admin._id} className="bg-white rounded-lg border p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold">{admin.name}</h3>
                      <p className="text-sm text-gray-600">{admin.email}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openBranchAccessModal(admin)}
                      >
                        <OfficeBuildingIcon className="w-4 h-4 mr-1" />
                        Branches
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openPermissionModal(admin)}
                      >
                        Configure
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}

        {activeTab === "approvals" && isRootAdmin && <ApprovalRequestsPanel />}
      </div>

      {/* Modals */}
      <Modal
        isOpen={isCreateModalOpen}
        closeModal={closeCreateModal}
        title="Create New Admin"
        className="max-w-2xl"
      >
        <AdminCreateModal onSubmit={handleCreateAdmin} branches={branches} />
      </Modal>

      <Modal
        isOpen={isResetPasswordModalOpen}
        closeModal={closeResetPasswordModal}
        title="Reset Admin Password"
      >
        {adminForReset && (
          <AdminResetPasswordModal
            onSubmit={handleResetPassword}
            admin={adminForReset}
          />
        )}
      </Modal>

      {adminForPermission && (
        <PermissionModal
          admin={adminForPermission}
          isOpen={isPermissionModalOpen}
          onClose={closePermissionModal}
        />
      )}

      {/* NEW: Branch Access Modal */}
      <BranchAccessModal
        admin={adminForBranchAccess}
        isOpen={isBranchAccessModalOpen}
        onClose={closeBranchAccessModal}
      />
    </>
  );
};

export default AdminManagement;
